import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../hive/ui/Icon';
import { useFairTracker } from '../data/FairTrackerStore';
import { Field, Input, Thumb, useToast } from '../ui';
import Qr from '../ui/Qr';
import { digits, planItemFound } from '../rules';
import { p } from '../routes';
import { tr } from '../i18n';

/**
 * Show to supplier (spec § 5.9) — the card the buyer physically holds up.
 *
 * Everything on this screen is for the SUPPLIER to read, which drives the
 * layout: Chinese is large and English small beneath it, the forwarder's
 * Chinese address is the biggest text on the page, and the questions are
 * numbered so a supplier can point at one.
 *
 * It is also the one screen that stays a single readable column on desktop —
 * it gets held up to a person, not scanned at a desk.
 */
export default function CardScreen() {
  const toast = useToast();
  const { fair, settings, saveSettings } = useFairTracker();

  const profile = (settings && settings.profile) || {};
  const complete = !!(profile.company || '').trim() && !!(profile.name || '').trim();

  const [tab, setTab] = useState('contact');
  const [draft, setDraft] = useState(profile);
  const [saving, setSaving] = useState(false);

  const questions = (settings && settings.cardQuestions) || [];
  const openPlan = ((fair && fair.plan) || []).filter((it) => !planItemFound(it));
  const forwarder = (fair && fair.forwarder) || {};
  const hasForwarder = !!(forwarder.name || forwarder.addressCN || forwarder.addressEN);

  const vcard = myVcard(profile);
  const waUrl = profile.whatsapp ? `https://wa.me/${digits(profile.whatsapp)}` : '';

  /* Inline setup when the profile is incomplete (spec § 5.9). */
  if (!complete) {
    const set = (k) => (v) => setDraft((d) => ({ ...d, [k]: v }));
    const save = async () => {
      setSaving(true);
      try {
        await saveSettings({ ...settings, profile: draft });
        toast('Card saved');
      } catch (e) {
        toast('Could not save');
      } finally {
        setSaving(false);
      }
    };
    return (
      <div className="ft-page ft-cardscreen">
        <div className="ft-title">
          <h1 className="ft-h1">{tr("Show to supplier")}</h1>
          <p className="ft-sub" style={{ margin: 0 }}>
            {tr("Fill this in once and it becomes the card you hold up at every booth.")}
          </p>
        </div>
        <section className="ft-card">
          <Field label={tr("Company *")}>
            <Input value={draft.company || ''} onChange={set('company')} />
          </Field>
          <Field label={tr("Your name *")}>
            <Input value={draft.name || ''} onChange={set('name')} />
          </Field>
          <div className="ft-grid-2">
            <Field label={tr("Title")}>
              <Input value={draft.title || ''} onChange={set('title')} />
            </Field>
            <Field label={tr("Phone")}>
              <Input value={draft.phone || ''} onChange={set('phone')} type="tel" />
            </Field>
            <Field label="WhatsApp">
              <Input value={draft.whatsapp || ''} onChange={set('whatsapp')} type="tel" />
            </Field>
            <Field label={tr("WeChat ID")}>
              <Input value={draft.wechat || ''} onChange={set('wechat')} />
            </Field>
            <Field label={tr("Email")}>
              <Input value={draft.email || ''} onChange={set('email')} type="email" />
            </Field>
            <Field label={tr("Website")}>
              <Input value={draft.website || ''} onChange={set('website')} />
            </Field>
            <Field label={tr("Port")}>
              <Input value={draft.port || ''} onChange={set('port')} placeholder="Houston, TX" />
            </Field>
          </div>
          <Field label={tr("Address")}>
            <Input value={draft.address || ''} onChange={set('address')} />
          </Field>
          <Field label={tr("Intro (English)")}>
            <Input value={draft.intro || ''} onChange={set('intro')} />
          </Field>
          <Field label="Intro (中文)" hint={tr("What the supplier reads first.")}>
            <Input value={draft.introZh || ''} onChange={set('introZh')} />
          </Field>
          <button
            type="button"
            className="ft-btn ft-btn-green ft-btn-block"
            disabled={saving || !(draft.company || '').trim() || !(draft.name || '').trim()}
            onClick={save}
          >
            {saving ? 'Saving…' : 'Save my card'}
          </button>
        </section>
      </div>
    );
  }

  const copyAsText = async () => {
    const text = [
      `${profile.company} — ${profile.name}${profile.title ? `, ${profile.title}` : ''}`,
      profile.introZh,
      profile.intro,
      profile.whatsapp ? `WhatsApp: +${digits(profile.whatsapp)}` : null,
      profile.wechat ? `WeChat: ${profile.wechat}` : null,
      profile.email,
      profile.website,
      profile.port ? `Port 港口: ${profile.port}` : null,
      profile.address,
    ]
      .filter(Boolean)
      .join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast('Card copied');
    } catch (e) {
      toast('Could not copy');
    }
  };

  const saveVcf = async () => {
    const blob = new Blob([vcard], { type: 'text/vcard' });
    const file = new File([blob], `${profile.name || 'contact'}.vcf`, { type: 'text/vcard' });
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: profile.name });
        return;
      }
    } catch (e) {
      if (e && e.name === 'AbortError') return;
    }
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast('Could not save the card');
    }
  };

  return (
    <div className="ft-page ft-cardscreen">
      {/* ── The buyer card ─────────────────────────────────────────────── */}
      <section className="ft-buyercard">
        <p className="ft-buyercard-eyebrow">BUYER · 采购商</p>
        <h1 className="ft-buyercard-company">{profile.company}</h1>
        <p className="ft-buyercard-name">
          {profile.name}
          {profile.title ? ` · ${profile.title}` : ''}
        </p>

        {profile.introZh && <p className="ft-zh-lg">{profile.introZh}</p>}
        {profile.intro && <p className="ft-en-sm">{profile.intro}</p>}

        <dl className="ft-buyercard-kv">
          {profile.whatsapp && (
            <>
              <dt>WhatsApp</dt>
              <dd>+{digits(profile.whatsapp)}</dd>
            </>
          )}
          {profile.wechat && (
            <>
              <dt>微信 WeChat</dt>
              <dd>{profile.wechat}</dd>
            </>
          )}
          {profile.email && (
            <>
              <dt>{tr("Email")}</dt>
              <dd>{profile.email}</dd>
            </>
          )}
          {profile.website && (
            <>
              <dt>{tr("Web")}</dt>
              <dd>{profile.website}</dd>
            </>
          )}
          {profile.port && (
            <>
              <dt>港口 Port</dt>
              <dd>{profile.port}</dd>
            </>
          )}
          {profile.address && (
            <>
              <dt>{tr("Address")}</dt>
              <dd>{profile.address}</dd>
            </>
          )}
        </dl>
      </section>

      {/* ── QR codes ───────────────────────────────────────────────────── */}
      <section className="ft-card">
        <div className="ft-tabs" style={{ margin: '0 0 12px' }}>
          <button
            type="button"
            className={`ft-tab-btn${tab === 'contact' ? ' ft-tab-btn-on' : ''}`}
            onClick={() => setTab('contact')}
          >
            Contact 名片
          </button>
          <button
            type="button"
            className={`ft-tab-btn${tab === 'wechat' ? ' ft-tab-btn-on' : ''}`}
            onClick={() => setTab('wechat')}
          >
            微信 WeChat
          </button>
          <button
            type="button"
            className={`ft-tab-btn${tab === 'whatsapp' ? ' ft-tab-btn-on' : ''}`}
            onClick={() => setTab('whatsapp')}
          >
            WhatsApp
          </button>
        </div>

        <div className="ft-qrwrap">
          {tab === 'contact' && <Qr value={vcard} size={200} label={tr("Scan to save my contact")} />}

          {tab === 'wechat' &&
            (profile.wechatQr ? (
              <Thumb photo={{ path: profile.wechatQr }} size={200} rounded={false} alt="WeChat QR" />
            ) : (
              <div className="ft-qr ft-qr-empty" style={{ width: 200, height: 200 }}>
                {/* A WeChat QR cannot be generated — only WeChat itself can
                    issue one, so it has to be uploaded (spec § 7). */}
                <span>
                  Upload your WeChat QR in{' '}
                  <Link to={`${p('/settings')}?open=card`}>{tr("Settings")}</Link>
                </span>
              </div>
            ))}

          {tab === 'whatsapp' &&
            (waUrl ? (
              <Qr value={waUrl} size={200} label={tr("Scan to message me on WhatsApp")} />
            ) : (
              <div className="ft-qr ft-qr-empty" style={{ width: 200, height: 200 }}>
                <span>{tr("Add a WhatsApp number to your card")}</span>
              </div>
            ))}
        </div>
      </section>

      {/* ── What we are looking for ────────────────────────────────────── */}
      {openPlan.length > 0 && (
        <section className="ft-card">
          <h2 className="ft-h2">We are looking for · 我们在找</h2>
          <ul className="ft-looking">
            {openPlan.map((it) => (
              <li key={it.id}>
                <b>{it.name}</b>
                <span className="ft-faint">
                  {[it.qty ? `${it.qty} pcs` : null, it.target ? `target ${it.target}` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Ship samples to ────────────────────────────────────────────── */}
      <section className="ft-card">
        <h2 className="ft-h2">Ship samples to · 样品寄到</h2>
        {hasForwarder ? (
          <>
            <p className="ft-sub" style={{ margin: '4px 0' }}>
              {[forwarder.name, forwarder.phone, forwarder.wechat ? `WeChat ${forwarder.wechat}` : null]
                .filter(Boolean)
                .join(' · ')}
            </p>
            {forwarder.addressCN && <p className="ft-zh-lg">{forwarder.addressCN}</p>}
            {forwarder.addressEN && <p className="ft-en-sm">{forwarder.addressEN}</p>}
          </>
        ) : (
          <p className="ft-sub">
            {tr('No forwarder saved for this fair.')}{' '}
            <Link to={p('/fairs')}>{tr('Add one in Edit fair')}</Link>{' '}
            {tr('and it appears here for the supplier to copy.')}
          </p>
        )}
      </section>

      {/* ── Questions ──────────────────────────────────────────────────── */}
      <section className="ft-card">
        <div className="ft-planhead">
          <h2 className="ft-h2">Questions · 问题</h2>
          <Link className="ft-btn ft-btn-sm" to={`${p('/settings')}?open=questions`}>
            {tr("Edit questions")}
          </Link>
        </div>
        <ol className="ft-questions">
          {questions.map((q) => (
            <li key={q.id}>
              <span className="ft-zh-lg">{q.zh}</span>
              <span className="ft-en-sm">{q.en}</span>
            </li>
          ))}
        </ol>
      </section>

      <div className="ft-btnrow" style={{ marginTop: 12 }}>
        <button type="button" className="ft-btn ft-btn-primary" onClick={saveVcf}>
          <Icon name="Download" size={16} /> {tr("Save my contact (.vcf)")}
        </button>
        <button type="button" className="ft-btn" onClick={copyAsText}>
          <Icon name="Copy" size={16} /> {tr("Copy as text")}
        </button>
      </div>
    </div>
  );
}

/** vCard 3.0 for the buyer's own card (spec § 7). */
function myVcard(profile) {
  const lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `N:${profile.name || ''}`,
    `FN:${profile.name || ''}`,
    `ORG:${profile.company || ''}`,
  ];
  if (profile.title) lines.push(`TITLE:${profile.title}`);
  if (profile.phone) lines.push(`TEL;TYPE=CELL:${profile.phone}`);
  if (profile.whatsapp) lines.push(`TEL;TYPE=WhatsApp:+${digits(profile.whatsapp)}`);
  if (profile.email) lines.push(`EMAIL:${profile.email}`);
  if (profile.website) lines.push(`URL:${profile.website}`);
  const note = [
    profile.wechat ? `WeChat: ${profile.wechat}` : null,
    profile.address || null,
  ]
    .filter(Boolean)
    .join(' | ');
  if (note) lines.push(`NOTE:${note}`);
  lines.push('END:VCARD');
  return lines.join('\r\n');
}
